import { useState } from "react";
import { ArrowDown, ArrowUp, Code2, Plus, Trash2 } from "lucide-react";
import {
  useCustomTrackersQuery,
  useCreateCustomTrackerMutation,
  useUpdateCustomTrackerMutation,
  useDeleteCustomTrackerMutation,
  useReorderCustomTrackersMutation,
  type CustomTracker,
  type TrackerPlacement,
} from "../../apollo/customTrackers";
import { useToastPush } from "../../components/ui/Toast";

const PLACEMENT_LABELS: Record<TrackerPlacement, string> = {
  head: "Head",
  body_start: "Body — top",
  body_end: "Body — bottom",
};

const INPUT_CLASS =
  "hair w-full rounded-md border bg-canvas/50 px-2.5 py-1.5 text-[12.5px] outline-none transition focus:border-tide/40";

// ── Row ─────────────────────────────────────

function TrackerRow({
  tracker,
  isFirst,
  isLast,
  busy,
  onMove,
  onSave,
  onToggleEnabled,
  onDelete,
}: {
  tracker: CustomTracker;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  onMove: (id: string, dir: -1 | 1) => void;
  onSave: (
    id: string,
    name: string,
    code: string,
    placement: TrackerPlacement
  ) => Promise<boolean>;
  onToggleEnabled: (id: string, enabled: boolean) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const [name, setName] = useState(tracker.name);
  const [code, setCode] = useState(tracker.code);
  const [placement, setPlacement] = useState<TrackerPlacement>(tracker.placement);

  const dirty =
    name !== tracker.name ||
    code !== tracker.code ||
    placement !== tracker.placement;

  const handleSave = async () => {
    if (!name.trim() || !code.trim() || busy) return;
    const trimmedName = name.trim();
    const ok = await onSave(tracker.id, trimmedName, code, placement);
    if (ok) {
      // The server persists the trimmed name, not the raw input value. Sync
      // local state to what was actually saved so `dirty` doesn't flip back
      // to true from leftover whitespace the moment the save resolves.
      setName(trimmedName);
    }
  };

  const handleToggleEnabled = () => {
    if (busy) return;
    onToggleEnabled(tracker.id, !tracker.enabled);
  };

  const handleDelete = () => {
    if (busy) return;
    if (!window.confirm(`Delete "${tracker.name}"? This stops it loading on your site.`)) {
      return;
    }
    onDelete(tracker.id, tracker.name);
  };

  return (
    <div className="hair-b border-b p-5 last:border-b-0">
      <div className="mb-3 flex items-center gap-2">
        <input
          className={INPUT_CLASS}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Tracker name, e.g. Google Tag Manager"
        />
        <select
          className={INPUT_CLASS + " w-auto"}
          value={placement}
          onChange={(e) => setPlacement(e.target.value as TrackerPlacement)}
        >
          {Object.entries(PLACEMENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onMove(tracker.id, -1)}
          disabled={isFirst || busy}
          aria-label="Move up"
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-4 hover:bg-canvas-2 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ArrowUp size={13} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={() => onMove(tracker.id, 1)}
          disabled={isLast || busy}
          aria-label="Move down"
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-4 hover:bg-canvas-2 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ArrowDown size={13} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          aria-label="Delete tracker"
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-4 hover:bg-rust-s/40 hover:text-rust disabled:cursor-not-allowed disabled:opacity-30"
        >
          <Trash2 size={13} strokeWidth={2} />
        </button>
      </div>

      <textarea
        className={INPUT_CLASS + " min-h-[120px] font-mono text-[11.5px]"}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Paste the snippet your vendor gave you, including the <script> tags."
        spellCheck={false}
      />

      <div className="mt-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-[12.5px] text-ink-3">
          <input
            type="checkbox"
            checked={tracker.enabled}
            disabled={busy}
            onChange={handleToggleEnabled}
          />
          Enabled
        </label>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || busy}
          className="rounded-md bg-tide px-3 py-1.5 text-[12.5px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-30"
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────

export function CustomTrackersPage() {
  const toast = useToastPush();
  const { data, loading, error, refetch } = useCustomTrackersQuery();
  const [createTracker] = useCreateCustomTrackerMutation();
  const [updateTracker] = useUpdateCustomTrackerMutation();
  const [deleteTracker] = useDeleteCustomTrackerMutation();
  const [reorderTrackers] = useReorderCustomTrackersMutation();

  // Single in-flight guard shared by every tracker mutation (add / save /
  // toggle / delete / reorder). It brackets the *whole* round trip — the
  // mutation plus the refetch that follows it — not just the mutation's own
  // Apollo `loading` flag. That's deliberate: the cross-row race this
  // guards against (see handleMove) lives in the window between a mutation
  // resolving and the refetch that refreshes `trackers` landing, and
  // per-mutation `loading` flags go false before that refetch completes.
  const [busy, setBusy] = useState(false);

  const trackers = data?.allCustomTrackers ?? [];

  const runGuarded = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () =>
    runGuarded(async () => {
      try {
        await createTracker({
          variables: {
            name: "New tracker",
            code: "<script>\n  // Paste your snippet here\n</script>",
            placement: "head",
            enabled: false,
            sortOrder: trackers.length,
          },
        });
        await refetch();
      } catch (err) {
        toast({
          kind: "error",
          title: "Couldn't add tracker",
          description: (err as Error).message,
        });
      }
    });

  const handleMove = (id: string, dir: -1 | 1) =>
    runGuarded(async () => {
      const index = trackers.findIndex((tracker) => tracker.id === id);
      const target = index + dir;
      if (index < 0 || target < 0 || target >= trackers.length) return;

      const next = [...trackers];
      [next[index], next[target]] = [next[target], next[index]];

      try {
        await reorderTrackers({ variables: { ids: next.map((t) => t.id) } });
        await refetch();
      } catch (err) {
        toast({
          kind: "error",
          title: "Couldn't reorder trackers",
          description: (err as Error).message,
        });
      }
    });

  const handleSaveTracker = async (
    id: string,
    name: string,
    code: string,
    placement: TrackerPlacement
  ): Promise<boolean> => {
    let ok = false;
    await runGuarded(async () => {
      try {
        await updateTracker({ variables: { id, name, code, placement } });
        toast({ kind: "success", title: `"${name}" saved` });
        await refetch();
        ok = true;
      } catch (err) {
        toast({
          kind: "error",
          title: "Couldn't save tracker",
          description: (err as Error).message,
        });
      }
    });
    return ok;
  };

  const handleToggleTracker = (id: string, enabled: boolean) =>
    runGuarded(async () => {
      try {
        await updateTracker({ variables: { id, enabled } });
        await refetch();
      } catch (err) {
        toast({
          kind: "error",
          title: "Couldn't update tracker",
          description: (err as Error).message,
        });
      }
    });

  const handleDeleteTracker = (id: string, name: string) =>
    runGuarded(async () => {
      try {
        await deleteTracker({ variables: { id } });
        toast({ kind: "success", title: `"${name}" deleted` });
        await refetch();
      } catch (err) {
        toast({
          kind: "error",
          title: "Couldn't delete tracker",
          description: (err as Error).message,
        });
      }
    });

  if (loading && trackers.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[13px] text-ink-5">Loading custom trackers…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
        Failed to load custom trackers: {error.message}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="col-span-12 lg:col-span-8">
        <div className="mb-8">
          <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
            Custom trackers
          </h1>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-3">
            Analytics and advertising snippets that load on your public website.
            Paste the code your vendor gives you — they run in the order listed,
            so keep your consent banner first. Trackers only run on your live
            site, never on staging.
          </p>
        </div>

        <section className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
          <div className="hair-b flex items-center justify-between border-b px-7 py-4">
            <h2 className="text-[14px] font-semibold text-ink">
              Trackers{trackers.length > 0 ? ` (${trackers.length})` : ""}
            </h2>
            <button
              type="button"
              onClick={handleAdd}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] text-ink-3 hover:bg-canvas-2 disabled:opacity-30"
            >
              <Plus size={13} strokeWidth={2} />
              Add tracker
            </button>
          </div>

          {trackers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-7 py-12 text-center">
              <Code2 size={20} strokeWidth={1.5} className="text-ink-5" />
              <div className="text-[13px] text-ink-3">No trackers yet</div>
              <div className="max-w-sm text-[12.5px] text-ink-5">
                Add one to load Google Tag Manager, a Meta pixel, or any other
                analytics snippet on your site.
              </div>
            </div>
          ) : (
            trackers.map((tracker, index) => (
              <TrackerRow
                key={tracker.id}
                tracker={tracker}
                isFirst={index === 0}
                isLast={index === trackers.length - 1}
                busy={busy}
                onMove={handleMove}
                onSave={handleSaveTracker}
                onToggleEnabled={handleToggleTracker}
                onDelete={handleDeleteTracker}
              />
            ))
          )}
        </section>
      </section>
    </div>
  );
}
